
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FaExternalLinkAlt } from "react-icons/fa";

interface SourceCardProps {
  url: string;
  credibility: number;
  summary: string;
  factors: { name: string; score: number; explanation: string }[];
}

export default function SourceCard({ url, credibility, summary, factors }: SourceCardProps) {
  const getCredibilityColor = (score: number) => {
    if (score >= 80) return "bg-green-500";
    if (score >= 60) return "bg-green-500/70";
    if (score >= 40) return "bg-yellow-500";
    if (score >= 20) return "bg-orange-500";
    return "bg-red-500";
  };

  // Format the domain for display
  const formatDomain = (url: string): string => {
    try {
      return new URL(url).hostname.replace(/^www\./, '');
    } catch {
      // If URL parsing fails, just return the original
      return url.replace(/^(https?:\/\/)?(www\.)?/, '');
    }
  };

  return (
    <Card className="border border-gray-800 bg-black/20 backdrop-blur-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold truncate max-w-xs md:max-w-sm">
            {formatDomain(url)}
          </CardTitle>
          <a 
            href={url.startsWith('http') ? url : `https://${url}`} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-truth hover:text-truth-light transition-colors"
          >
            <FaExternalLinkAlt />
          </a>
        </div>
        <CardDescription>
          <span className="flex items-center gap-2">
            <span className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden inline-block">
              <span 
                className={`h-full ${getCredibilityColor(credibility)} inline-block`}
                style={{ width: `${credibility}%` }}
              ></span>
            </span>
            <span className="font-medium">{credibility}%</span>
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="text-sm text-gray-300">{summary}</div>
        </div>
        <div>
          <h3 className="text-sm font-medium mb-2">Credibility Factors</h3>
          <div className="space-y-3">
            {factors.map((factor, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium">{factor.name}</span>
                  <Badge variant={factor.score > 50 ? "default" : "destructive"} className="text-xs">
                    {factor.score}%
                  </Badge>
                </div>
                <div className="text-xs text-gray-400">{factor.explanation}</div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
      <CardFooter className="text-xs text-gray-400 pt-0">
        Last analyzed: {new Date().toLocaleDateString()}
      </CardFooter>
    </Card>
  );
}
